import React, { useState, useEffect, useCallback } from "react";
import { MdCheckCircle, MdEdit, MdQuestionAnswer } from "react-icons/md";
import Card from "components/card";
import Avatar from "components/avatar";
import Button from "components/button";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import { PageContainer, PageHeader } from "components/page";
import { Textarea } from "components/fields";
import teamService from "services/teamService";
import { getApiErrorMessage } from "api";
import notify from "lib/notify";
import { formatDate, formatDateTime } from "utils/format";

const SCALE_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({ value: n, label: String(n) }));
const YES_NO_OPTIONS = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

/** One answer picked from a small set of choices, as a row of radio pills. */
const ChoiceGroup = ({ name, legend, options, value, onChange }) => (
  <fieldset>
    <legend className="mb-2 text-sm font-semibold text-fg">{legend}</legend>
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <label key={String(opt.value)} className="cursor-pointer">
          <input
            type="radio"
            name={name}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            className="peer sr-only"
          />
          <span className="flex h-11 min-w-[44px] items-center justify-center rounded-control border border-line px-4 text-sm font-semibold text-fg-muted transition-colors hover:border-accent hover:text-fg peer-checked:border-primary peer-checked:bg-primary peer-checked:text-primary-fg peer-focus-visible:ring-2 peer-focus-visible:ring-focus peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface">
            {opt.label}
          </span>
        </label>
      ))}
    </div>
  </fieldset>
);

const QuestionField = ({ question, value, onChange }) => {
  const name = `question-${question.id}`;
  if (question.questionType === "scale") {
    return (
      <ChoiceGroup
        name={name}
        legend={question.prompt}
        options={SCALE_OPTIONS}
        value={value}
        onChange={onChange}
      />
    );
  }
  if (question.questionType === "yes_no") {
    return (
      <ChoiceGroup
        name={name}
        legend={question.prompt}
        options={YES_NO_OPTIONS}
        value={value}
        onChange={onChange}
      />
    );
  }
  if (question.questionType === "select") {
    return (
      <ChoiceGroup
        name={name}
        legend={question.prompt}
        options={(question.options || []).map((opt) => ({ value: opt, label: opt }))}
        value={value}
        onChange={onChange}
      />
    );
  }
  return (
    <Textarea
      label={question.prompt}
      rows={3}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Type your answer…"
    />
  );
};

const MyCheckIn = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [editingExisting, setEditingExisting] = useState(false);
  const [formError, setFormError] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await teamService.getMyCheckIn();
      setData(res.data);
      if (res.data?.submission) {
        const prefill = {};
        (res.data.submission.answers || []).forEach((a) => {
          prefill[a.questionId] = a.answer;
        });
        setAnswers(prefill);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const setAnswer = (questionId, value) => {
    setFormError("");
    setAnswers((a) => ({ ...a, [questionId]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = (data?.questions || [])
      .filter((q) => answers[q.id] !== undefined && answers[q.id] !== "")
      .map((q) => ({ questionId: q.id, answer: answers[q.id] }));

    if (!payload.length) {
      setFormError("Answer at least one question before submitting.");
      return;
    }

    try {
      setSubmitting(true);
      await teamService.submitCheckIn({ answers: payload });
      notify.success("Check-in submitted");
      setEditingExisting(false);
      fetchData();
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not submit check-in"));
    } finally {
      setSubmitting(false);
    }
  };

  // Group questions by the leader who asked them
  const grouped = (data?.questions || []).reduce((acc, q) => {
    const key = q.leader ? `${q.leader.firstName} ${q.leader.lastName}` : "Your Leadership";
    if (!acc[key]) acc[key] = [];
    acc[key].push(q);
    return acc;
  }, {});

  const alreadySubmitted = Boolean(data?.submission) && !editingExisting;

  let content;
  if (error) {
    content = (
      <ErrorState
        message={getApiErrorMessage(error, "We couldn't load your check-in.")}
        onRetry={fetchData}
      />
    );
  } else if (loading && !data) {
    content = (
      <div className="flex flex-col gap-5" aria-busy="true">
        <Skeleton className="h-56 w-full rounded-card" />
        <Skeleton className="h-40 w-full rounded-card" />
      </div>
    );
  } else if (!data?.questions?.length) {
    content = (
      <Card padding="md">
        <EmptyState
          icon={MdQuestionAnswer}
          title="No Check-In Questions This Week"
          description={
            data?.submission
              ? "You've already checked in, and there's nothing new to answer."
              : "Your leaders haven't asked any questions yet, or you're not on a team in the org chart."
          }
        />
      </Card>
    );
  } else if (alreadySubmitted) {
    content = (
      <Card padding="md">
        <EmptyState
          icon={MdCheckCircle}
          title="You're Checked In for This Week"
          description={`Submitted ${formatDateTime(data.submission.submittedAt)}`}
          action={
            <Button
              variant="secondary"
              leftIcon={<MdEdit className="h-4 w-4" />}
              onClick={() => setEditingExisting(true)}
            >
              Edit My Answers
            </Button>
          }
        />
      </Card>
    );
  } else {
    content = (
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
        {Object.entries(grouped).map(([leaderName, questions]) => {
          const leader = questions[0].leader;
          return (
            <Card key={leaderName} as="section" padding="md" className="gap-5">
              <div className="flex items-center gap-3">
                <Avatar src={leader?.avatar} name={leaderName} size="md" noBorder />
                <div>
                  <h2 className="text-sm font-bold text-fg">{leaderName}</h2>
                  <p className="text-xs text-fg-muted">{leader?.jobTitle || "Asks:"}</p>
                </div>
              </div>

              <div className="flex flex-col gap-6">
                {questions.map((q) => (
                  <QuestionField
                    key={q.id}
                    question={q}
                    value={answers[q.id]}
                    onChange={(v) => setAnswer(q.id, v)}
                  />
                ))}
              </div>
            </Card>
          );
        })}

        {formError && (
          <p role="alert" className="text-right text-sm text-danger-fg">
            {formError}
          </p>
        )}

        <div className="flex justify-end gap-2 pb-6">
          {editingExisting && (
            <Button variant="secondary" onClick={() => setEditingExisting(false)}>
              Cancel
            </Button>
          )}
          <Button type="submit" size="lg" loading={submitting}>
            {data?.submission ? "Update Check-In" : "Submit Check-In"}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <PageContainer variant="narrow">
      <PageHeader
        title="My Check-In"
        description={
          data
            ? `Week of ${formatDate(data.weekStart)}. Your answers go to the leaders who asked each question.`
            : "Your answers go to the leaders who asked each question."
        }
      />
      {content}
    </PageContainer>
  );
};

export default MyCheckIn;
